/**
 * Recovers from a stale-chunk error after a deploy.
 *
 * HQ is a Vite/React Router SPA served as an Appwrite Sites static deployment
 * (see appwrite.config.json `hq`), which falls back unknown paths to
 * `index.html`. Each deploy replaces the whole asset set with new content
 * hashes; it does not keep the previous deploy's chunk files around. A tab
 * left open across a deploy still holds the OLD `index.html`, which references
 * chunk filenames that no longer exist. When React Router lazily imports one
 * of those routes, the request 404s into the SPA fallback and the browser
 * gets `index.html` (MIME `text/html`) back for a `.js` request, which it
 * refuses to execute as a module — surfacing as "error loading dynamically
 * imported module" / "Failed to fetch dynamically imported module", caught by
 * React Router's default ErrorBoundary. See work item #89.
 *
 * The fix is simply to reload: a fresh `index.html` references the current
 * (working) chunk hashes. `sessionStorage` guards against reloading forever
 * if the deployment is genuinely broken, and self-clears once the app has
 * been running long enough to prove the current chunk set is healthy, so a
 * LATER deploy can still trigger one more self-heal in the same tab.
 */

const RELOAD_FLAG = 'panelgear-hq-chunk-reload';

/** How long a page must run without a chunk error before it counts as healthy. */
const HEALTHY_AFTER_MS = 10_000;

const CHUNK_ERROR_PATTERNS = [
  /error loading dynamically imported module/i,
  /failed to fetch dynamically imported module/i,
  /importing a module script failed/i,
];

export function isChunkLoadError(message: unknown): boolean {
  return typeof message === 'string' && CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

function readReloadFlag(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_FLAG) === '1';
  } catch {
    // sessionStorage can throw (private browsing, blocked site data). Treat as
    // "not yet reloaded" — worst case is one extra reload, not a stuck screen.
    return false;
  }
}

function writeReloadFlag(value: boolean): void {
  try {
    if (value) sessionStorage.setItem(RELOAD_FLAG, '1');
    else sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // ignore — see readReloadFlag
  }
}

/**
 * Reloads the page once to pick up the current deploy's `index.html` and
 * chunk hashes. A second call in the same tab session (before the healthy
 * timer clears the flag) is a no-op, so a genuinely broken deployment shows
 * its error instead of reload-looping.
 */
export function reloadForChunkError(): void {
  if (readReloadFlag()) return;
  writeReloadFlag(true);
  window.location.reload();
}

/**
 * Wires up the window-level listeners. Call once, as early as possible, from
 * the app entrypoint.
 */
export function installChunkErrorReload(): void {
  // Vite's own signal for a failed dynamic import or modulepreload.
  window.addEventListener('vite:preloadError', () => reloadForChunkError());

  // Belt and braces for the same failure surfacing as an ordinary rejection or
  // error event rather than Vite's custom one.
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason as { message?: unknown } | undefined;
    if (isChunkLoadError(reason?.message ?? reason)) reloadForChunkError();
  });
  window.addEventListener('error', (event) => {
    if (isChunkLoadError(event.message)) reloadForChunkError();
  });

  // Reaching this point without a chunk error means the current asset set is
  // healthy, so clear the guard for any later deploy that happens during this
  // same tab session.
  setTimeout(() => writeReloadFlag(false), HEALTHY_AFTER_MS);
}
